// © Microsoft Corporation. All rights reserved.
// Provides utility functions to get and set cookies

import { ConnectorNamespaces, CookieConsentProvider, CookieConsentStatus, SameSiteValues } from "@msnews/experiences-constants";
import { OneTrustCookieCategory, OneTrustCookieCategoryMapping } from "@msnews/cookie-consent-common";

import { CookieRelatedErrors } from "@msnews/diagnostics";
import { ExperiencesSharedStateConnector } from "@msnews/experiences-shared-state";
import { PageBase } from "@msnews/experiences-redux";
import { Telemetry } from "@msnews/telemetry-contracts";
import { setRequireFunctionalCookie } from "@msnews/core";

// Functional cookies can be written irrespective of the cookie consent status.
// NOTE: These should always be in lower case (preferably in alpha-numeric order)
const functionalCookies = [
    "aace",
    "app_anon",
    "at",
    "cac",
    "cbypass",
    "child",
    "ddlcur",
    "hidebn",
    "ipt",
    "mktreset",
    "nactk",
    "ntps",
    "preferencesmsn",
    "smd",
    "sptmarket"
];

/**
 * Helper function used to set a cookie
 * When expiryDays is equal to -1 it will unset the cookie
 * * @param {string} name Cookie name
 * * @param {string} value Cookie value
 * * @param {number} expiryDays Cookie expiry days
 * * @param {string} domain Cookie domain
 * * @param {string} path Cookie path
 * * @param {boolean} secure Is it a https only cookie
 * * @param {OneTrustCookieCategory} cookieCategory The OneTrust category the given cookie belongs to
 * * @param {sameSiteValue} sameSiteValue The optional value to use for samesite cookie property
 * * @returns {boolean} indicate if cookie saved successfully
 */
export function setCookie(name: string, value: string, expiryDays?: number, domain?: string, path?: string, secure?: boolean, cookieCategory?: OneTrustCookieCategory, sameSiteValue?: SameSiteValues): boolean {
    if (expiryDays > 0 && !isSetCookiePermitted(name, cookieCategory)) {
        return false;
    }

    setRequireFunctionalCookie(name, value, expiryDays, domain, path, secure, sameSiteValue);
    return true;
}

/**
 * Helper function used to check if cookie setting is permitted
 * * @param {string} cookieName Cookie name
 * * @param {OneTrustCookieCategory} cookieCategoryOverride The OneTrust category override provided by the setCookie caller
 * * @returns {boolean} True is cookie setting permitter, false otherwise
 */
function isSetCookiePermitted(cookieName: string, cookieCategoryOverride?: OneTrustCookieCategory): boolean {
    // If cookie consent status is "NotRequired", cookies can be written.
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;

    if (!sharedStateConnector) {
        Telemetry.sendAppErrorEvent({ ...CookieRelatedErrors.SharedStateConnectorNull, message: "SharedStateConnector is null" });
        return false;
    }

    const currentSharedState = sharedStateConnector.getCurrentState();
    const consentStatus: CookieConsentStatus = currentSharedState.cookieConsentStatus;
    const consentProvider: CookieConsentProvider = currentSharedState.cookieConsentProvider;

    let isSetAllowed = false;
    switch (consentProvider) {
        case CookieConsentProvider.OneTrustBanner: {

            isSetAllowed = isCookieInOneTrustActiveCategory(cookieName, currentSharedState.activeCookieCategories, cookieCategoryOverride);
            break;
        }
        case CookieConsentProvider.MSCCBanner:
        case CookieConsentProvider.CookieWall: {

            isSetAllowed = isCookieInFunctionalCategory(cookieName, consentStatus);
            break;
        }
        default:
            isSetAllowed = consentStatus === CookieConsentStatus.NotRequired;
            break;
    }

    // if we reach here, we do not allow cookie to be set by returning false
    return isSetAllowed;
}

/**
 * Check (case-sensitive check) if cookie is in OneTrust active categories
 *
 * @param cookieName Cookie name
 * @param activeCategories The active categories returned by OneTrust API
 * @param cookieCategoryOverride The OneTrust category override provided by the setCookie caller
 */
function isCookieInOneTrustActiveCategory(cookieName: string, activeCategories: Array<OneTrustCookieCategory>, cookieCategoryOverride?: OneTrustCookieCategory): boolean {

    if (cookieCategoryOverride) {

        // check if the provided cookie category override is in the active categories returned by OneTrust
        if (activeCategories.includes(cookieCategoryOverride)) {
            return true;
        }

    } else {
        // If no override is provided, check the hardcoded cookie mapping in the active cookie categories
        for (const activeCategory of activeCategories) {
            const categoryCookies = OneTrustCookieCategoryMapping[activeCategory];

            if (categoryCookies.includes(cookieName)) {
                return true;
            }
        }
    }

    // Check if the cookieName is present in the remaining cookie categories and fire an app error if not present
    let cookieCategorized = false;
    const remainingCategories = Object.keys(OneTrustCookieCategoryMapping).filter(category => !activeCategories.includes(OneTrustCookieCategory[category]));

    // iterate through remaining category values
    for (const remainingCategory of remainingCategories) {
        const categoryCookies = OneTrustCookieCategoryMapping[remainingCategory];

        if (categoryCookies.includes(cookieName)) {
            cookieCategorized = true;
            break;
        }
    }

    if (!cookieCategorized) {
        Telemetry.sendAppErrorEvent({
            ...CookieRelatedErrors.CookieNotCategorized,
            message: `Attempting to write a cookie: "${cookieName}" not present in the cookie category mapping.`
        });
    }

    return false;
}

/**
 * Check (case-insensitive check) if cookie is in Functional category
 * @param cookieName Cookie name
 * @param consentStatus Cookie consent status
 */
function isCookieInFunctionalCategory(cookieName: string, consentStatus: CookieConsentStatus) {
    if (consentStatus === CookieConsentStatus.NotRequired) {
        return true;
    }

    const lowerCaseCookieName = cookieName.toLowerCase();
    if (functionalCookies.indexOf(lowerCaseCookieName) >= 0) {
        return true;
    }

    return false;
}